import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadConfigurationFactors } from '../api/configuration';
import { listExchangeSourcesForPair } from '../api/exchangeSources';
import type { ExchangeSourceOption } from '../models/exchangeSource';
import {
  pickDefaultExchangeSource,
  sortExchangeSourcesForDisplay,
} from '../utils/exchangeSourceDefaults';

export const MANUAL_ORG_SOURCE_ID = '__org_manual__';

export interface ExchangeRateWidgetState {
  loading: boolean;
  currency: string;
  referenceCurrency: string;
  sources: ExchangeSourceOption[];
  selectedSourceId: string;
  setSelectedSourceId: (id: string) => void;
  activeRate: number | null;
  activeSourceName: string;
  lastFetchedAt?: Date;
}

export function useExchangeRateWidget(
  organizationId: string | null | undefined
): ExchangeRateWidgetState {
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('VES');
  const [referenceCurrency, setReferenceCurrency] = useState('USD');
  const [orgExchangeRate, setOrgExchangeRate] = useState(36);
  const [orgExchangeSourceId, setOrgExchangeSourceId] = useState('');
  const [sources, setSources] = useState<ExchangeSourceOption[]>([]);
  const [selectedSourceId, setSelectedSourceId] = useState('');

  const load = useCallback(async (orgId: string) => {
    setLoading(true);
    try {
      const factors = await loadConfigurationFactors(orgId);
      const fromCurrency = factors.referenceCurrency || 'USD';
      const toCurrency = factors.currency || 'VES';
      setCurrency(toCurrency);
      setReferenceCurrency(fromCurrency);
      setOrgExchangeRate(
        typeof factors.exchangeRate === 'number' &&
          Number.isFinite(factors.exchangeRate)
          ? factors.exchangeRate
          : 36
      );
      setOrgExchangeSourceId(factors.exchangeSourceId || '');

      let listed: ExchangeSourceOption[] = [];
      try {
        listed = await listExchangeSourcesForPair({
          fromCurrency,
          toCurrency,
        });
      } catch (e) {
        console.warn('Could not load exchange sources for widget', e);
      }

      const sorted = sortExchangeSourcesForDisplay(listed);
      setSources(sorted);

      const orgMatch = factors.exchangeSourceId
        ? sorted.find(s => s.id === factors.exchangeSourceId)
        : undefined;
      const fallback = pickDefaultExchangeSource(sorted);
      const initialId = orgMatch?.id || fallback?.id || MANUAL_ORG_SOURCE_ID;
      setSelectedSourceId(initialId);
    } catch (e) {
      console.warn('Could not load exchange rate widget data', e);
      setSources([]);
      setSelectedSourceId(MANUAL_ORG_SOURCE_ID);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setSources([]);
      setSelectedSourceId('');
      return;
    }
    void load(organizationId);
  }, [organizationId, load]);

  const selectedSource = useMemo(
    () => sources.find(s => s.id === selectedSourceId),
    [sources, selectedSourceId]
  );

  const activeRate = useMemo(() => {
    if (
      selectedSource &&
      typeof selectedSource.rate === 'number' &&
      Number.isFinite(selectedSource.rate) &&
      selectedSource.rate > 0
    ) {
      return selectedSource.rate;
    }
    if (
      selectedSourceId === MANUAL_ORG_SOURCE_ID ||
      sources.length === 0
    ) {
      return Number.isFinite(orgExchangeRate) && orgExchangeRate > 0
        ? orgExchangeRate
        : null;
    }
    return null;
  }, [selectedSource, selectedSourceId, sources.length, orgExchangeRate]);

  const activeSourceName = useMemo(() => {
    if (selectedSource?.name?.trim()) return selectedSource.name.trim();
    if (selectedSourceId === MANUAL_ORG_SOURCE_ID || sources.length === 0) {
      return orgExchangeSourceId ? 'Tasa de la organización' : 'Manual';
    }
    return '—';
  }, [
    selectedSource,
    selectedSourceId,
    sources.length,
    orgExchangeSourceId,
  ]);

  return {
    loading,
    currency,
    referenceCurrency,
    sources,
    selectedSourceId,
    setSelectedSourceId,
    activeRate,
    activeSourceName,
    lastFetchedAt: selectedSource?.lastFetchedAt,
  };
}
